import { useState } from "react";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import dayjs from "dayjs";

/**
 * DatePickerField
 *
 * A controlled MUI DateTimePicker that formats the selected date+time as:
 *   "YYYY-MM-DDTHH:mm:ss"  e.g. "2025-11-26T14:30:00"
 *
 * Props:
 *   value       {string|null}  - controlled ISO string value (or null)
 *   onChange    {Function}     - called with the formatted string (or null if cleared)
 *   label       {string}       - field label (default: "Select Date & Time")
 *   minDateTime {string|null}  - optional min datetime as ISO string
 *   maxDateTime {string|null}  - optional max datetime as ISO string
 *   disabled    {boolean}
 */
export default function DatePickerField({
  value = null,
  onChange,
  label = "Select Date & Time",
  minDateTime = null,
  maxDateTime = null,
  disabled = false,
}) {
  // Convert incoming ISO string → dayjs (or null)
  const dayjsValue = value ? dayjs.unix(value) : null;

  const handleChange = (newDayjsValue) => {
    if (!newDayjsValue || !newDayjsValue.isValid()) {
      onChange?.(null);
      return;
    }
    onChange?.(newDayjsValue.unix()); // e.g. 1732622400
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DateTimePicker
        label={label}
        value={dayjsValue}
        onChange={handleChange}
        disabled={disabled}
        minDateTime={minDateTime ? dayjs(minDateTime) : undefined}
        maxDateTime={maxDateTime ? dayjs(maxDateTime) : undefined}
        slotProps={{
          textField: {
            fullWidth: true,
            variant: "outlined",
          },
        }}
      />
    </LocalizationProvider>
  );
}
